import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../../../ui/Model';
import { isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import { TicketCategoriesModel } from '../../../shared/models/ticket.model';
import { getTicketCategories } from '../../../shared/services/data/ticket.data';
import { categoryGroups } from '../../../shared/services/helpers/ticket-rules.helper';
import { CHIP, PRIORITY_STYLE } from '../../shared/ticket-visuals';

interface Props {
  show: boolean;
  onClose: () => void;
}

const EMERGENCY: Record<string, string> = { always: 'Always emergency', never: '', optional: '' };

/**
 * Who each ticket type goes to (CX feedback, 2026-10-14: "how do we see the routing?").
 * Read-only: routing is the taxonomy's owner chain, not a Zammad trigger. A ticket starts
 * with the chain's first team and moves along it by handoff; changing its type keeps it
 * with its team. Editing a chain is a taxonomy change, asked of the tech team.
 */
export const RoutingModal: React.FC<Props> = ({ show, onClose }) => {
  const [doc, setDoc] = useState<TicketCategoriesModel | undefined>();
  const [text, setText] = useState('');

  useEffect(() => {
    if (!show || doc) return;
    getTicketCategories().then((result) => {
      if (!isErrorInfo(result)) setDoc(result);
    });
  }, [show, doc]);

  const groups = useMemo(() => {
    const needle = text.trim().toLowerCase();
    return (doc ? categoryGroups(doc) : [])
      .map((g) => ({
        ...g,
        options: g.options.filter(
          (o) =>
            !needle ||
            [o.label, ...o.item.ownerChain.flatMap((s) => s.teams)].some((v) =>
              v.toLowerCase().includes(needle)
            )
        )
      }))
      .filter((g) => g.options.length);
  }, [doc, text]);

  return (
    <Modal
      isOpen={show}
      onClose={onClose}
      containerClassName="lg:h-fit lg:max-h-[88vh] lg:w-[900px]"
      headerContent={
        <div className="text-center font-gilroy-bold text-2xl leading-[100%] text-neutral-500">
          Ticket routing
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-neutral-400">
          Each ticket type starts with the first team in its chain, and is handed off along it.
          Emailed-in tickets land with CX until someone sets their type. To change a chain, ask the
          tech team: it is part of the ticket taxonomy, not a setting in the helpdesk.
        </p>
        <input
          className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-neutral-500"
          placeholder="Search a type or a team"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        {!doc ? <p className="text-sm text-neutral-400">Loading…</p> : null}
        {groups.map((g) => (
          <section key={g.label} className="flex flex-col gap-2">
            <h3 className="font-gilroy-bold text-neutral-500">{g.label}</h3>
            <table className="w-full text-left text-sm">
              <tbody>
                {g.options.map((o) => (
                  <tr key={o.value} className="border-t border-gray-100 align-top">
                    <td className="py-2 pr-3 text-neutral-500">{o.label}</td>
                    <td className="whitespace-nowrap py-2 pr-3">
                      <span className={`${CHIP} ${PRIORITY_STYLE[o.item.priority]}`}>
                        {o.item.priority}
                      </span>
                      {EMERGENCY[o.item.emergency] ? (
                        <span className={`${CHIP} ml-1 bg-secondary-500 text-white`}>
                          {EMERGENCY[o.item.emergency]}
                        </span>
                      ) : null}
                    </td>
                    <td className="py-2 text-neutral-400">
                      {o.item.ownerChain
                        .map((s) => `${s.teams.join(' / ')}${s.when ? ` (${s.when})` : ''}`)
                        .join(' → ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ))}
      </div>
    </Modal>
  );
};
